/**
 * questionRules — the question editor's rules, pure: the steps, the form a
 * question loads into and saves from, what each type asks for, and what each
 * step still needs before the author moves on.
 *
 * The page (AddEditQuizQuestionPage) holds the form and walks the steps; nothing
 * here touches React, the router or the API.
 */

export const STEPS = [
  { key: "topic", title: "Topic", hint: "Where it goes in the bank" },
  { key: "type", title: "Type", hint: "How students answer" },
  { key: "question", title: "Question", hint: "What students read" },
  { key: "answer", title: "Answer", hint: "What counts as correct" },
  { key: "marks", title: "Marks & notes", hint: "Scoring and explanation" },
  { key: "review", title: "Review", hint: "Check and save" },
];

export const stepIndex = (key) => STEPS.findIndex((s) => s.key === key);

export const QUESTION_TYPES = [
  { value: "single_choice", label: "Single choice", hint: "One correct answer from a list" },
  { value: "multi_select", label: "Multiple answers", hint: "Tick every correct answer" },
  { value: "true_false", label: "True / False", hint: "A statement that is true or false" },
  { value: "text_answer", label: "Short answer", hint: "A typed word or phrase" },
  { value: "fill_blank", label: "Fill in the blank", hint: "The missing word in a sentence" },
  { value: "numeric_answer", label: "Numeric", hint: "A number, within a tolerance" },
  { value: "image_mcq", label: "Image choice", hint: "The question or options are pictures" },
  { value: "code_conceptual", label: "Code reading", hint: "Read a snippet, pick the answer" },
];

export const typeLabel = (value) => QUESTION_TYPES.find((t) => t.value === value)?.label || value;

/** The "Code reading" type belongs to Coding Practice: off there, it is not offered. */
export const typesFor = ({ coding = true } = {}) =>
  (coding ? QUESTION_TYPES : QUESTION_TYPES.filter((t) => t.value !== "code_conceptual"));

export const CODE_LANGUAGES = [
  { value: "javascript", label: "JavaScript" },
  { value: "python", label: "Python" },
  { value: "java", label: "Java" },
  { value: "c", label: "C" },
  { value: "cpp", label: "C++" },
  { value: "sql", label: "SQL" },
  { value: "typescript", label: "TypeScript" },
  { value: "go", label: "Go" },
];

export const DIFFICULTIES = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

export const SINGLE_TYPES = ["single_choice", "true_false", "image_mcq", "code_conceptual"];
export const CHOICE_TYPES = ["single_choice", "multi_select", "true_false", "image_mcq", "code_conceptual"];
export const TEXT_TYPES = ["text_answer", "fill_blank"];

/** What the Answer step edits for a type: options, typed answers, or a number. */
export const answerKind = (type) => {
  if (CHOICE_TYPES.includes(type)) return "options";
  if (TEXT_TYPES.includes(type)) return "text";
  if (type === "numeric_answer") return "numeric";
  return "none";
};

export const letterOf = (i) => String.fromCodePoint(65 + i);

let seq = 0;
export const newOption = (patch = {}) => {
  seq += 1;
  return { text: "", imageUrl: "", isCorrect: false, _id: `${Date.now()}_${seq}`, ...patch };
};

const TRUE_FALSE = () => [
  { text: "True", imageUrl: "", isCorrect: true, _id: "tf-true" },
  { text: "False", imageUrl: "", isCorrect: false, _id: "tf-false" },
];
const fourOptions = () => [newOption(), newOption(), newOption(), newOption()];
const isTrueFalsePair = (options) => (options || []).length === 2
  && options.every((o) => o._id === "tf-true" || o._id === "tf-false");

export function emptyForm() {
  return {
    category: "",
    subcategory: "",
    tags: [],
    questionText: "",
    questionType: "single_choice",
    imageUrl: "",
    options: fourOptions(),
    acceptedAnswers: [""],
    codeSnippet: "",
    codeLanguage: "javascript",
    numericAnswer: "",
    numericTolerance: 0,
    difficulty: "medium",
    marks: 1,
    negativeMarks: 0,
    explanation: "",
    hint: "",
    source: "",
    estimatedTime: 60,
    // Library (marketplace) authoring only: false holds the question back even
    // when its folder is published.
    isPublic: true,
    // The passage or case this question is asked about (its id), if any.
    passage: "",
  };
}

/** A saved question, as the editor's form. */
export function formFromQuestion(q) {
  return {
    category: q.category?._id || q.category || "",
    subcategory: q.subcategory?._id || q.subcategory || "",
    tags: q.tags || [],
    questionText: q.questionText || "",
    questionType: q.questionType || "single_choice",
    imageUrl: q.imageUrl || "",
    options: q.options?.length > 0 ? q.options : [newOption(), newOption()],
    acceptedAnswers: q.acceptedAnswers?.length > 0 ? q.acceptedAnswers : [""],
    codeSnippet: q.codeSnippet || "",
    codeLanguage: q.codeLanguage || "javascript",
    numericAnswer: q.numericAnswer ?? "",
    numericTolerance: q.numericTolerance || 0,
    difficulty: q.difficulty || "medium",
    marks: q.marks || 1,
    negativeMarks: q.negativeMarks || 0,
    explanation: q.explanation || "",
    hint: q.hint || "",
    source: q.source || "",
    estimatedTime: q.estimatedTime || 60,
    isPublic: q.isPublic !== false,
    passage: q.passage?._id || q.passage || "",
  };
}

/**
 * The form with another type. True / False gets its fixed pair; leaving it, or
 * arriving at a choice type with no options, gets four blank ones; a
 * single-answer type keeps only the first correct option, because the server
 * refuses a single choice with two.
 */
export function changeType(form, type) {
  const next = { ...form, questionType: type };
  if (type === "true_false") {
    next.options = TRUE_FALSE();
  } else if (CHOICE_TYPES.includes(type)) {
    if (form.options.length < 2 || isTrueFalsePair(form.options)) next.options = fourOptions();
    if (SINGLE_TYPES.includes(type)) {
      const first = next.options.findIndex((o) => o.isCorrect);
      next.options = next.options.map((o, i) => (o.isCorrect && i !== first ? { ...o, isCorrect: false } : o));
    }
  }
  return next;
}

/** One option changed. Marking one correct in a single-answer type unmarks the rest. */
export function setOption(form, index, patch) {
  const single = SINGLE_TYPES.includes(form.questionType) && patch.isCorrect === true;
  return {
    ...form,
    options: form.options.map((o, i) => {
      if (i === index) return { ...o, ...patch };
      return single ? { ...o, isCorrect: false } : o;
    }),
  };
}

export const addOption = (form) => ({ ...form, options: [...form.options, newOption()] });
export const removeOption = (form, index) => (form.options.length <= 2
  ? form
  : { ...form, options: form.options.filter((_, i) => i !== index) });

export const setAnswer = (form, index, value) => ({
  ...form,
  acceptedAnswers: form.acceptedAnswers.map((a, i) => (i === index ? value : a)),
});
export const addAnswer = (form) => ({ ...form, acceptedAnswers: [...form.acceptedAnswers, ""] });
export const removeAnswer = (form, index) => (form.acceptedAnswers.length <= 1
  ? form
  : { ...form, acceptedAnswers: form.acceptedAnswers.filter((_, i) => i !== index) });

export function addTag(form, raw) {
  const t = String(raw || "").trim();
  return t && !form.tags.includes(t) ? { ...form, tags: [...form.tags, t] } : form;
}
export const removeTag = (form, tag) => ({ ...form, tags: form.tags.filter((t) => t !== tag) });

/** An option with neither words nor a picture is a blank button in the exam. */
function emptyOptionProblem(form) {
  if (!CHOICE_TYPES.includes(form.questionType) || form.questionType === "true_false") return "";
  const i = form.options.findIndex((o) => !String(o.text || "").trim() && !o.imageUrl);
  if (i === -1) return "";
  return form.questionType === "image_mcq"
    ? `Option ${letterOf(i)} is empty — type its text or add an image.`
    : `Option ${letterOf(i)} is empty — type its text or remove it.`;
}

const notNumber = (v) => v === "" || v === null || v === undefined || Number.isNaN(Number(v));

/**
 * What a step still needs, as `{ field, message }` — `field` names the control
 * the message belongs under. The Type and Review steps need nothing of their own.
 */
export function stepProblems(form, key) {
  const out = [];
  if (key === "topic" && !form.category) {
    out.push({ field: "category", message: "Choose the topic this question goes in." });
  }
  if (key === "question" && !String(form.questionText || "").trim()) {
    out.push({ field: "questionText", message: "Type the question." });
  }
  if (key === "answer") {
    const kind = answerKind(form.questionType);
    if (kind === "options") {
      if (!form.options.some((o) => o.isCorrect)) {
        out.push({
          field: "options",
          message: form.questionType === "multi_select" ? "Tick every correct answer." : "Tick the correct answer.",
        });
      }
      const blank = emptyOptionProblem(form);
      if (blank) out.push({ field: "options", message: blank });
    }
    if (kind === "text" && !form.acceptedAnswers.some((a) => String(a).trim())) {
      out.push({ field: "acceptedAnswers", message: "Add at least one answer that counts as correct." });
    }
    if (kind === "numeric" && notNumber(form.numericAnswer)) {
      out.push({ field: "numericAnswer", message: "Enter the correct number." });
    }
  }
  if (key === "marks") {
    if (Number(form.marks) < 0) out.push({ field: "marks", message: "Marks cannot be negative." });
    if (Number(form.negativeMarks) < 0) {
      out.push({ field: "negativeMarks", message: "Enter what a wrong answer loses as a positive number, or 0." });
    }
  }
  return out;
}

/** Every step's problems, each tagged with its step. */
export const allProblems = (form) =>
  STEPS.flatMap((s) => stepProblems(form, s.key).map((p) => ({ ...p, step: s.key })));

/** The first step with something to fix, or null. */
export const firstProblemStep = (form) => allProblems(form)[0]?.step || null;

/** Field → message for one step, the first message per field. */
export function fieldErrors(problems) {
  const out = {};
  for (const p of problems) if (p.field && !out[p.field]) out[p.field] = p.message;
  return out;
}

const isObjectId = (v) => /^[0-9a-f]{24}$/i.test(String(v || ""));

/**
 * The options as the server stores them. An option made here carries a local
 * `_id` for its React key, which the server refuses — it casts option ids to
 * ObjectId, so one "1730000000_1" or "tf-true" fails the whole save. A saved
 * option keeps its id, because students' answers are graded against it. A type
 * without options sends none, not the blank four it was created with.
 */
const optionsPayload = (form) => (CHOICE_TYPES.includes(form.questionType)
  ? form.options.map(({ _id, ...o }) => (isObjectId(_id) ? { ...o, _id } : o))
  : []);

/** The body POST/PUT /quiz-questions takes. */
export const buildPayload = (form) => ({
  ...form,
  options: optionsPayload(form),
  subcategory: form.subcategory || undefined,
  acceptedAnswers: form.acceptedAnswers.filter((a) => String(a).trim()),
  // null detaches a question from its passage on an edit.
  passage: form.passage || null,
});

/**
 * "Save & add another": the next question in the same place, of the same kind,
 * worth the same — topic, type, marks, difficulty, time, tags, source and
 * passage are kept, the question and its answer start blank.
 */
export function nextQuestion(form) {
  let options = form.options;
  if (form.questionType === "true_false") options = TRUE_FALSE();
  else if (CHOICE_TYPES.includes(form.questionType)) options = fourOptions();
  return {
    ...form,
    questionText: "",
    imageUrl: "",
    options,
    acceptedAnswers: [""],
    codeSnippet: "",
    numericAnswer: "",
    explanation: "",
    hint: "",
  };
}

/** The answer key in a sentence, for the review. */
export function answerSummary(form) {
  const kind = answerKind(form.questionType);
  if (kind === "options") {
    const correct = form.options
      .map((o, i) => (o.isCorrect ? letterOf(i) : null))
      .filter(Boolean);
    if (!correct.length) return "No correct answer marked yet";
    return correct.length === 1 ? `${correct[0]} is correct` : `${correct.join(", ")} are correct`;
  }
  if (kind === "text") {
    const n = form.acceptedAnswers.filter((a) => String(a).trim()).length;
    return n ? `${n} accepted answer${n === 1 ? "" : "s"} (any case)` : "No accepted answer yet";
  }
  if (kind === "numeric") {
    if (notNumber(form.numericAnswer)) return "No number yet";
    return Number(form.numericTolerance) > 0
      ? `${form.numericAnswer} (± ${form.numericTolerance})`
      : `${form.numericAnswer}, exactly`;
  }
  return "—";
}

/** "2 marks · −0.5 for a wrong answer", for the review and the preview. */
export function marksSummary(form) {
  const marks = Number(form.marks) || 0;
  const neg = Number(form.negativeMarks) || 0;
  const main = `${marks} mark${marks === 1 ? "" : "s"}`;
  return neg > 0 ? `${main} · −${neg} for a wrong answer` : `${main} · no negative marking`;
}
